/**
 * Effects Rendering
 * 
 * Visual effects for squares and UI elements.
 */

import type { Square, GridState } from '../types/square.js';

/**
 * Logo animation state
 */
export interface LogoState {
  rotationAngle: number; // Current rotation angle in degrees (0-360)
  lastRotationTime: number; // Time of last completed rotation
  rotationInterval: number; // Interval between rotations in milliseconds (10000 = 10 seconds)
  isRotating: boolean; // Whether logo is currently rotating
  rotationDuration: number; // Duration of rotation animation in milliseconds
  rotationStartTime: number; // Time when current rotation started
  // Reserved for future warning states
  heartbeatScale: number;
  heartbeatPhase: number;
}

/**
 * LED flash animation state for the circle between "Pivot" and "is"
 */
export interface LedFlashState {
  flashIntensity: number; // 0.0 to 1.0 - current flash brightness
  lastFlashTime: number; // Time of last flash
  flashInterval: number; // Interval between flashes in milliseconds (e.g., 15000 for 15 seconds)
  flashDuration: number; // Duration of each flash in milliseconds
}

/**
 * Create initial logo state
 */
export function createLogoState(): LogoState {
  const now = performance.now();
  return {
    rotationAngle: 0,
    lastRotationTime: now - 10000, // Start with rotation ready to happen soon
    rotationInterval: 10000, // 10 seconds
    isRotating: false,
    rotationDuration: 1000, // 1 second rotation duration
    rotationStartTime: 0,
    heartbeatScale: 1.0,
    heartbeatPhase: 0,
  };
}

/**
 * Create initial LED flash state
 */
export function createLedFlashState(): LedFlashState {
  const now = performance.now();
  // Set initial flash time so first flash happens after ~2 seconds
  // Then subsequent flashes happen every 15 seconds
  return {
    flashIntensity: 0.0,
    lastFlashTime: now - 13000, // 13 seconds ago, so next flash in ~2 seconds
    flashInterval: 15000, // 15 seconds - average for computer monitor LEDs
    flashDuration: 100, // 100ms flash duration
  };
}

/**
 * Update LED flash animation state
 */
export function updateLedFlashState(ledState: LedFlashState, now: number): void {
  const timeSinceLastFlash = now - ledState.lastFlashTime;

  // If it's time for a new flash cycle
  if (timeSinceLastFlash >= ledState.flashInterval) {
    ledState.lastFlashTime = now;
    ledState.flashIntensity = 1.0; // Start flash at full intensity
  } else if (timeSinceLastFlash < ledState.flashDuration) {
    // During flash: fade out quickly (exponential decay)
    const elapsed = timeSinceLastFlash / ledState.flashDuration;
    ledState.flashIntensity = Math.max(0, 1.0 - elapsed * elapsed); // Exponential fade
  } else {
    // After flash: remain off
    ledState.flashIntensity = 0.0;
  }
}

/**
 * Update logo animation state
 */
export function updateLogoState(logoState: LogoState, now: number): void {
  const timeSinceLastRotation = now - logoState.lastRotationTime;

  // Check if it's time to start a new rotation
  if (!logoState.isRotating && timeSinceLastRotation >= logoState.rotationInterval) {
    logoState.isRotating = true;
    logoState.rotationStartTime = now;
  }

  // Update rotation angle if rotating
  if (logoState.isRotating) {
    const rotationElapsed = now - logoState.rotationStartTime;
    const rotationProgress = Math.min(rotationElapsed / logoState.rotationDuration, 1.0);

    // Use ease-in-out for smooth rotation
    const easedProgress = rotationProgress < 0.5
      ? 2 * rotationProgress * rotationProgress
      : 1 - Math.pow(-2 * rotationProgress + 2, 2) / 2;

    logoState.rotationAngle = easedProgress * 360;

    // Check if rotation is complete
    if (rotationProgress >= 1.0) {
      logoState.isRotating = false;
      logoState.rotationAngle = 0;
      logoState.lastRotationTime = now;
    }
  } else {
    // Logo is static (no rotation)
    logoState.rotationAngle = 0;
  }

  // Reset heartbeat values for future warning states
  logoState.heartbeatScale = 1.0;
  logoState.heartbeatPhase = 0;
}

/**
 * Draw logo with animations
 */
export function drawLogo(
  ctx: CanvasRenderingContext2D,
  logoImage: HTMLImageElement,
  logoState: LogoState,
  width: number
): void {
  const logoMaxSize = 56 * 1.13; // 13% bigger (63.28)
  const logoPadding = 20;

  // Maintain aspect ratio
  const logoAspect = logoImage.width / logoImage.height;
  let logoWidth: number, logoHeight: number;

  if (logoAspect > 1) {
    logoWidth = logoMaxSize;
    logoHeight = logoMaxSize / logoAspect;
  } else {
    logoHeight = logoMaxSize;
    logoWidth = logoMaxSize * logoAspect;
  }

  // Calculate logo position - top left, aligned with text baseline
  const logoX = logoPadding + 2; // Moved 2px right
  // Align logo so its bottom aligns roughly with text baseline for visual harmony
  const textY = logoPadding;
  const textFontSize = 24;
  const textBaselineY = textY + textFontSize; // Approximate text baseline
  // Position logo so it aligns nicely with text - slight offset for better alignment
  // Nudged down to accommodate larger scale
  const logoY = textY + (textFontSize - logoHeight) / 2 + 6.5; // Moved 2px down (was 4.5)
  const logoCenterX = logoX + logoWidth / 2;
  const logoCenterY = logoY + logoHeight / 2;

  ctx.save();

  // Draw lens flare when rotating (placeholder for image-based lens flare)
  // TODO: Replace with subtle lens flare images when provided
  if (logoState.isRotating) {
    // Reserved space for lens flare image rendering
    // Will be implemented when lens flare images are provided
  }

  ctx.globalAlpha = 0.9;

  // Apply Y-axis rotation transformation (door hinge effect) if rotating
  if (logoState.isRotating && logoState.rotationAngle > 0) {
    const angleRad = (logoState.rotationAngle * Math.PI) / 180;
    const horizontalScale = Math.abs(Math.cos(angleRad));

    ctx.translate(logoCenterX, logoCenterY);
    ctx.scale(horizontalScale, 1.0);
    ctx.translate(-logoCenterX, -logoCenterY);
  }

  // Draw logo image
  ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);

  ctx.restore();
}

/**
 * Draw "pivot • the decision engine" text
 * Supports rolling drum effect with multiple words at different positions
 */
export function drawPivotText(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  ledState?: LedFlashState,
  textColor?: string, // Optional theme text color
  rotatingWord?: string, // Optional word to replace "decision" (defaults to "decision")
  rollingWords?: Array<{ word: string; offset: number; opacity: number }> // Optional rolling words with offsets
): void {
  ctx.save();
  // Use theme text color if provided, otherwise fallback to default
  const baseColor = textColor || 'rgba(15, 17, 21, 0.85)';
  ctx.textBaseline = 'alphabetic'; // Use alphabetic baseline for proper alignment

  // Use universal font scaling system (more conservative for title)
  // Title should not scale as aggressively - keep it closer to base size
  const titleFontSize = 24; // Keep title at base size (brand should be consistent)
  const taglineFontSize = 20; // Keep tagline at base size

  // Draw "pivot" in Crimson Pro
  ctx.font = `500 ${titleFontSize}px "Crimson Pro", serif`;
  ctx.fillText('pivot', x, y);
  const pivotWidth = ctx.measureText('pivot').width;

  // Draw bullet separator (same font as title for consistency)
  const bulletX = x + pivotWidth + 4;
  ctx.fillText('•', bulletX, y);
  const bulletWidth = ctx.measureText('•').width;

  // Calculate tagline position
  const taglineX = bulletX + bulletWidth + 4;
  const taglineY = y; // Base Y position for tagline

  // Set tagline font
  ctx.font = `400 ${taglineFontSize}px "Manrope", sans-serif`;

  // If rolling words are provided, draw them with offsets and opacities
  if (rollingWords && rollingWords.length > 0) {
    // Draw "the " prefix - use full opacity if settled, subtle if rolling
    ctx.fillStyle = baseColor;
    const isRolling = rollingWords.length > 1 || rollingWords[0].offset !== 0;
    ctx.globalAlpha = isRolling ? 0.5 : 1.0;
    ctx.fillText('the ', taglineX, taglineY);
    const theWidth = ctx.measureText('the ').width;
    
    // Draw each rolling word at its offset position
    // Sort by offset to draw in correct order (bottom to top)
    const sortedWords = [...rollingWords].sort((a, b) => b.offset - a.offset);
    for (const wordData of sortedWords) {
      if (wordData.opacity > 0) {
        ctx.save();
        ctx.fillStyle = baseColor;
        ctx.globalAlpha = wordData.opacity;
        const wordY = taglineY + wordData.offset;
        ctx.fillText(wordData.word, taglineX + theWidth, wordY);
        ctx.restore();
      }
    }
    
    // Draw " engine" suffix - use full opacity if settled, subtle if rolling
    ctx.fillStyle = baseColor;
    ctx.globalAlpha = isRolling ? 0.5 : 1.0;
    // Calculate width of current word (the one closest to center) for positioning
    const centerWord = rollingWords.reduce((prev, curr) => 
      Math.abs(curr.offset) < Math.abs(prev.offset) ? curr : prev
    );
    const wordWidth = ctx.measureText(centerWord.word).width;
    ctx.fillText(' engine', taglineX + theWidth + wordWidth, taglineY);
  } else {
    // Simple mode: just draw the text normally
    const word = rotatingWord || 'decision';
    ctx.fillStyle = baseColor;
    ctx.globalAlpha = 1;
    ctx.fillText(`the ${word} engine`, taglineX, taglineY);
  }

  ctx.restore();
}

/**
 * Draw debug visualization: red dots in element centers
 */
export function drawDebugSquareCenters(
  ctx: CanvasRenderingContext2D,
  elements: Array<{ state: { hidden: boolean; px: { x: number; y: number; w: number; h: number } } }>
): void {
  ctx.save();
  ctx.fillStyle = 'rgba(255, 0, 0, 0.8)';

  for (const e of elements) {
    if (e.state.hidden) continue;

    const centerX = e.state.px.x + e.state.px.w / 2;
    const centerY = e.state.px.y + e.state.px.h / 2;

    ctx.beginPath();
    ctx.arc(centerX, centerY, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

/**
 * Draw grid verification visualization: x times y squares with dimensions in center
 * 
 * This is a "brainwashing technique" to verify grid congruity:
 * - Draws cols × rows squares matching the grid dimensions
 * - Each cell uses exactly the same x and y values (cellW × cellH, equilateral)
 * - Displays the exact cell dimensions (cellW × cellH) as text in the center
 * 
 * This verifies that:
 * 1. Grid dimensions match (cols × rows)
 * 2. All cells are equilateral (cellW === cellH)
 * 3. Cell dimensions are consistent across all cells
 */
export function drawGridVerification(
  ctx: CanvasRenderingContext2D,
  gridState: GridState
): void {
  ctx.save();

  // Draw squares for each cell in the grid
  for (let row = 0; row < gridState.rows; row++) {
    for (let col = 0; col < gridState.cols; col++) {
      // Calculate cell position using EXACT same formula as grid allocation
      // Formula: gutterX + col * (cellW + gap) for x
      // Formula: gutterY + row * (cellH + gap) for y
      const x = gridState.gutterX + col * (gridState.cellW + gridState.gap);
      const y = gridState.gutterY + row * (gridState.cellH + gridState.gap);

      // Each cell uses exactly the same dimensions (cellW × cellH, equilateral)
      const cellW = gridState.cellW;
      const cellH = gridState.cellH;

      // Draw square/cell background
      ctx.save();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)'; // Subtle white background
      ctx.strokeStyle = 'rgba(0, 255, 0, 0.6)'; // Green border for visibility
      ctx.lineWidth = 1;

      ctx.beginPath();
      ctx.rect(x, y, cellW, cellH);
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      // Draw cell dimensions as text in the center
      ctx.save();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'; // White text
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Calculate center position
      const centerX = x + cellW / 2;
      const centerY = y + cellH / 2;

      // Format dimensions text (cellW × cellH)
      // Round to 1 decimal place for readability
      const wText = cellW.toFixed(1);
      const hText = cellH.toFixed(1);
      const dimensionsText = `${wText} × ${hText}`;

      // Use a readable font size based on cell size
      // Scale font size to fit nicely in cell (about 1/8th of cell height)
      const fontSize = Math.max(10, Math.min(cellH / 8, 20));
      ctx.font = `${fontSize}px "Manrope", sans-serif`;

      // Draw dimensions text in center
      ctx.fillText(dimensionsText, centerX, centerY);

      // Also draw grid coordinates (col, row) above dimensions
      const coordText = `(${col}, ${row})`;
      ctx.font = `${Math.max(8, Math.min(fontSize * 0.6, 14))}px "Manrope", sans-serif`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)'; // Slightly more transparent for coordinates
      ctx.fillText(coordText, centerX, centerY - fontSize * 1.2);

      ctx.restore();
    }
  }

  ctx.restore();
}

/**
 * Draw avoidance area visualization: shows bounding boxes of areas we're avoiding
 * 
 * Shows:
 * - Logo/title bounding box (top-left area)
 * - Gutter areas (top, bottom, left, right)
 * - Grid workspace boundary
 */
export function drawAvoidanceArea(
  ctx: CanvasRenderingContext2D,
  gridState: GridState,
  logoImage: HTMLImageElement | null,
  logoLoaded: boolean
): void {
  ctx.save();

  const LOGO_MAX_SIZE = 56 * 1.13; // 63.28px
  const LOGO_PADDING = 20;
  const TEXT_FONT_SIZE = 24;

  // Calculate logo/title bounding box using EXACT positions from rendering code
  // Match canvas.ts textY calculation and effects.ts drawLogo logoY calculation
  let logoX = LOGO_PADDING + 2; // From drawLogo: logoPadding + 2
  let logoY = LOGO_PADDING; // Will be calculated below
  let logoWidth = 0;
  let logoHeight = 0;
  let textX = LOGO_PADDING;
  let textY = 38; // Default from canvas.ts (when no logo)

  if (logoLoaded && logoImage) {
    const logoAspect = logoImage.width / logoImage.height;
    logoHeight = logoAspect > 1 ? LOGO_MAX_SIZE / logoAspect : LOGO_MAX_SIZE;
    logoWidth = logoAspect > 1 ? LOGO_MAX_SIZE : LOGO_MAX_SIZE * logoAspect;

    // Position text to the right of logo with better spacing (matches canvas.ts)
    textX = LOGO_PADDING + logoWidth + 20;

    // Fine-tune vertical position - move up to align better with logo, then down by 18px (matches canvas.ts)
    textY = LOGO_PADDING + (logoHeight - TEXT_FONT_SIZE) / 2 - 14 + 18; // Moved down by 18px (was -14, now +4)

    // Calculate logo Y position (EXACT match from effects.ts drawLogo)
    const textYForLogo = LOGO_PADDING;
    logoY = textYForLogo + (TEXT_FONT_SIZE - logoHeight) / 2 + 6.5; // Moved 2px down (was 4.5)
  }

  // Calculate text width (matches drawPivotText logic)
  ctx.font = `500 ${TEXT_FONT_SIZE}px "Crimson Pro", serif`;
  const pivotWidth = ctx.measureText('pivot').width;
  const bulletWidth = ctx.measureText('•').width;
  ctx.font = `400 ${20}px "Manrope", sans-serif`; // Tagline font size
  const taglineWidth = ctx.measureText('the decision engine').width;
  const totalTextWidth = pivotWidth + bulletWidth + taglineWidth + 4 + 4; // +4 for spacing between parts

  // Calculate actual content bounding box (logo + text only, no extra padding)
  // This shows the actual visual footprint of the logo/title content
  // Use the topmost and leftmost positions, and bottommost/rightmost positions
  const logoBottom = logoY + logoHeight;
  const textBottom = textY + TEXT_FONT_SIZE; // Text baseline is at textY, so bottom is textY + font size
  const logoRight = logoX + logoWidth;
  const textRight = textX + totalTextWidth;

  // The avoidance area should show the actual content bounds
  // Start at the top-leftmost point of content
  const avoidanceX = Math.min(logoX, textX);
  const avoidanceY = Math.min(logoY, textY);

  // Width: extend to the rightmost edge of content (logo or text)
  const avoidanceWidth = Math.max(logoRight, textRight) - avoidanceX;

  // Height: extend to the bottommost edge of content (logo or text)
  // This is the actual visual height, not the footprint with padding
  const avoidanceHeight = Math.max(logoBottom, textBottom) - avoidanceY;

  // Draw logo/title bounding box (avoidance area)
  ctx.strokeStyle = 'rgba(255, 100, 100, 0.8)'; // Red/orange for avoidance
  ctx.fillStyle = 'rgba(255, 100, 100, 0.15)'; // Subtle red fill
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 4]); // Dashed line for avoidance area

  ctx.beginPath();
  ctx.rect(avoidanceX, avoidanceY, avoidanceWidth, avoidanceHeight);
  ctx.fill();
  ctx.stroke();

  // Draw label for avoidance area
  ctx.save();
  ctx.fillStyle = 'rgba(255, 100, 100, 0.9)';
  ctx.font = '12px "Manrope", sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.setLineDash([]); // Reset dash
  ctx.fillText('Logo/Title Avoidance Area', avoidanceX + 4, avoidanceY + 4);
  ctx.restore();

  // Draw gutter areas (top, bottom, left, right)
  ctx.strokeStyle = 'rgba(255, 200, 100, 0.6)'; // Orange for gutters
  ctx.fillStyle = 'rgba(255, 200, 100, 0.1)'; // Subtle orange fill
  ctx.lineWidth = 1.5;
  ctx.setLineDash([2, 2]); // Dashed line for gutters

  // Top gutter
  ctx.beginPath();
  ctx.rect(0, 0, gridState.width, gridState.gutterY);
  ctx.fill();
  ctx.stroke();

  // Bottom gutter
  ctx.beginPath();
  ctx.rect(0, gridState.height - gridState.gutterY, gridState.width, gridState.gutterY);
  ctx.fill();
  ctx.stroke();

  // Left gutter
  ctx.beginPath();
  ctx.rect(0, 0, gridState.gutterX, gridState.height);
  ctx.fill();
  ctx.stroke();

  // Right gutter
  ctx.beginPath();
  ctx.rect(gridState.width - gridState.gutterX, 0, gridState.gutterX, gridState.height);
  ctx.fill();
  ctx.stroke();

  // Draw grid workspace boundary (the area where grid can be placed)
  const workspaceX = gridState.gutterX;
  const workspaceY = gridState.gutterY;
  const workspaceWidth = gridState.width - 2 * gridState.gutterX;
  const workspaceHeight = gridState.height - 2 * gridState.gutterY;

  ctx.strokeStyle = 'rgba(100, 255, 100, 0.8)'; // Green for workspace
  ctx.fillStyle = 'rgba(100, 255, 100, 0.05)'; // Subtle green fill
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 2]); // Different dash pattern for workspace

  ctx.beginPath();
  ctx.rect(workspaceX, workspaceY, workspaceWidth, workspaceHeight);
  ctx.fill();
  ctx.stroke();

  // Draw label for workspace
  ctx.save();
  ctx.fillStyle = 'rgba(100, 255, 100, 0.9)';
  ctx.font = '12px "Manrope", sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.setLineDash([]); // Reset dash
  ctx.fillText('Grid Workspace', workspaceX + 4, workspaceY + 4);
  ctx.restore();

  // Draw gutter labels
  ctx.save();
  ctx.fillStyle = 'rgba(255, 200, 100, 0.9)';
  ctx.font = '11px "Manrope", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.setLineDash([]);

  // Top gutter label
  ctx.fillText(`Top: ${gridState.gutterY}px`, gridState.width / 2, gridState.gutterY / 2);

  // Bottom gutter label
  ctx.fillText(`Bottom: ${gridState.gutterY}px`, gridState.width / 2, gridState.height - gridState.gutterY / 2);

  // Left gutter label (rotated)
  ctx.save();
  ctx.translate(gridState.gutterX / 2, gridState.height / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(`Left: ${gridState.gutterX}px`, 0, 0);
  ctx.restore();

  // Right gutter label (rotated)
  ctx.save();
  ctx.translate(gridState.width - gridState.gutterX / 2, gridState.height / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(`Right: ${gridState.gutterX}px`, 0, 0);
  ctx.restore();

  ctx.restore();

  ctx.restore();
}

/**
 * Dismiss button hover state (per square ID)
 */
const dismissButtonHoverState: Map<string, boolean> = new Map();

/**
 * Set dismiss button hover state for a square
 * 
 * Used by BaseView and ViewManager to track hover state for dismiss buttons.
 */
export function setDismissButtonHover(squareId: string, hover: boolean): void {
  dismissButtonHoverState.set(squareId, hover);
}

/**
 * REMOVED: drawSquareBackgrounds() - Consolidated into BaseView rendering
 * 
 * Square backgrounds are now rendered by each view on its own canvas via BaseView.renderToCanvas().
 * This eliminates the split rendering system and ensures unified rendering.
 * 
 * REMOVED: drawDismissButton_DEPRECATED() - No longer needed
 * 
 * Dismiss buttons are now rendered by each view via BaseView.renderDismissButton()
 * which uses drawDismissButton from interactions.ts.
 */

